import { ChangeDetectorRef, Component, DestroyRef, effect, EffectRef, ElementRef, inject, OnInit, Signal, ViewChild } from '@angular/core';
import { DvmService } from '@services/dvm.service';
import { PreviewComponent } from './preview/preview.component';
import { Viewer3dEvent, Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Viewer3dRelatedPreviewObject, Viewer3dRelatedPreviewsEventObject } from '@3ddv/dvm-internal';
import anime from 'animejs';
import { RelatedPreviewsService } from '@services/components/related-previews.service';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';

@Component({
    selector: 'app-related-previews',
    imports: [ PreviewComponent],
    standalone: true,
    templateUrl: './related-previews.component.html',
    styleUrls: ['./related-previews.component.css']
})
export class RelatedPreviewsComponent implements OnInit {

  // VIEWCHILDREN
  @ViewChild('previewsContainer')
  private readonly previewsContainer!:     ElementRef<HTMLDivElement>;
  

  // SERVICES
  private readonly dvmService:             DvmService                                 = inject(DvmService);
  private readonly relatedPreviewsService: RelatedPreviewsService                     = inject(RelatedPreviewsService);
  private readonly destroyRef:             DestroyRef                                 = inject(DestroyRef);
  private readonly cdr:                    ChangeDetectorRef                          = inject(ChangeDetectorRef);


  // GLOBAL STATE                   
  private readonly view:                   Signal<AppViews | null>                    = this.dvmService.view;
  private readonly viewer3d:               Viewer3dService                            = this.dvmService.viewer3d;
    
    
  // COMPONENT STATE     
  private showElements:                    boolean                                    = false;
  private requestedShowElements:           boolean                                    = false;
  private readonly relatedPreviews:        Map<string, Viewer3dRelatedPreviewObject>  = new Map<string, Viewer3dRelatedPreviewObject>();
  protected previews:                      Viewer3dRelatedPreviewObject[]             = [];
  protected prevPreview:                   Viewer3dRelatedPreviewObject | null        = null;
  private animationFrame:                  number | null                              = null;
  private animationRequest:                number                                     = 0;
  private changeDetectionRequest:          number                                     = 0;


  // EFFECTS
  private onViewChange:                    EffectRef                                  = effect((): void => {
   
    const currentView: AppViews | null = this.view();
   
    if(!currentView) return;

    this.relatedPreviewsService.resetCurrentPreview();

    if(currentView !== AppViews.Pano && this.showElements) this.togglePreviews(false);
    
  });

  // LIFECYCLE METHODS
  ngOnInit(): void {
    
    this.initComponent();

  }

  // METHODS
  private initComponent(): void {
    
    this.viewer3d.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.initSuscriptions());

  }

  private initSuscriptions(): void {

    // UPDATE RELATED PREVIEWS
    this.viewer3d.getObservable('update_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: Viewer3dEvent<Viewer3dRelatedPreviewsEventObject>): void => this.updateRelatedPreviews(event['event']['related_previews']));

    // SHOW RELATED PREVIEWS
    this.viewer3d.getObservable('show_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => this.togglePreviews(true));

    // HIDE RELATED PREVIEWS
    this.viewer3d.getObservable('hide_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => this.togglePreviews(this.view() === AppViews.Pano));

    // RESET RELATED PREVIEWS
    this.viewer3d.getObservable('reset_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => this.togglePreviews(this.viewer3d.getVenueId() === this.dvmService.currentVenue));

  }

  private updateRelatedPreviews(relatedPreview: Viewer3dRelatedPreviewObject): void  {

    // TEMP FIX
    if(this.relatedPreviews.has(relatedPreview.id)) {

      const isSameView: boolean = this.relatedPreviews.get(relatedPreview.id)?.view_id === relatedPreview.view_id;

      if(!isSameView) this.prevPreview = this.relatedPreviews.get(relatedPreview.id) ?? null;
        

    }

    this.relatedPreviews.set(relatedPreview.id, relatedPreview);
    this.syncPreviews();

    if(this.requestedShowElements || this.showElements) this.scheduleAnimation(true);
    
  }
  
  private togglePreviews(show: boolean): void {

    this.requestedShowElements = show;

    if(this.showElements === show) return;

    if(show && this.previews.length === 0) return;

    this.scheduleAnimation(show);

  }

  private scheduleAnimation(show: boolean): void {

    const animationRequest: number = ++this.animationRequest;

    if(this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);

    this.animationFrame = requestAnimationFrame((): void => {
      this.animationFrame = null;
      if(animationRequest !== this.animationRequest) return;

      this.cdr.markForCheck();
      this.animationFrame = requestAnimationFrame((): void => {
        this.animationFrame = null;
        this.animatePreviews(show);
      });
    });

  }

  private animatePreviews(show: boolean): void {

    const targets = Array.from(this.previewsContainer.nativeElement.children);

    if(targets.length === 0) return;

    anime.remove(targets);

    anime({
        targets: targets,
        opacity: show ? 1 : 0,
        translateY: show ? [20, 0] : [0, 20],
        easing: 'easeInOutQuad',
        delay: anime.stagger(100),
        duration: 300,
        complete: (): void => { 
            if(!show) {
                this.relatedPreviews.clear();
                this.syncPreviews();
                this.showElements = false;
                this.requestedShowElements = false;
            } else {
                this.showElements = true;
                this.requestedShowElements = true;
            }

            this.cdr.markForCheck();
        } 
    });

  }

  private syncPreviews(): void {

    this.previews = Array.from(this.relatedPreviews.values());

    const changeDetectionRequest: number = ++this.changeDetectionRequest;

    requestAnimationFrame((): void => {
      if(changeDetectionRequest !== this.changeDetectionRequest) return;
      this.cdr.markForCheck();
    });

  }

  protected checkPreviews(preview: Viewer3dRelatedPreviewObject): void {

    this.relatedPreviewsService.currentPreview.set(preview);

    if(this.prevPreview) {
      this.prevPreview.id = preview.id;
      this.relatedPreviews.set(preview.id, this.prevPreview);
      this.prevPreview = preview;
      this.syncPreviews();
    }

  }

  protected handlePreviewError(preview: Viewer3dRelatedPreviewObject): void {
    
    // Removemos el preview que falló del Map
    this.relatedPreviews.delete(preview.id);
    this.syncPreviews();
    
    // Si no quedan previews, ocultamos el contenedor
    if (this.relatedPreviews.size === 0) {
      this.showElements = false;
      this.requestedShowElements = false;
      this.cdr.markForCheck();
    }
    
  }

}
